import React, { useState, useEffect } from 'react';
import './EmailInput.css';

/**
 * EmailInput Component - Supports multiple email addresses with validation
 * @param {string} value - Current email string (comma or semicolon separated)
 * @param {function} onChange - Callback function when emails change
 * @param {string} placeholder - Placeholder text
 * @param {boolean} readOnly - Whether the input is read-only
 * @param {boolean} required - Whether the field is required
 * @param {boolean} showValidationStatus - Whether to show validation status indicators
 * @param {boolean} showHelpText - Whether to show help text
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
const EmailInput = ({
    value = '',
    onChange,
    placeholder = 'Enter email addresses (separate with comma or semicolon)',
    readOnly = false,
    required = false,
    showValidationStatus = true,
    showHelpText = true,
    className = '',
    style = {},
    ...rest
}) => {
    const [validationState, setValidationState] = useState({
        isValid: true,
        emails: [],
        invalidEmails: [],
        errors: []
    });

    // Email validation function
    const validateEmailAddresses = (emailString) => {
        if (!emailString || emailString.trim() === '') {
            return { 
                isValid: !required, // Only valid if not required
                emails: [], 
                invalidEmails: [],
                errors: required ? ['Email is required'] : []
            };
        }

        // Split by comma or semicolon and trim whitespace
        const emailList = emailString
            .split(/[,;]/)
            .map(email => email.trim())
            .filter(email => email.length > 0);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const validEmails = [];
        const invalidEmails = [];

        emailList.forEach(email => {
            if (emailRegex.test(email)) {
                validEmails.push(email);
            } else {
                invalidEmails.push(email);
            }
        });

        return {
            isValid: invalidEmails.length === 0 && (!required || validEmails.length > 0),
            emails: validEmails,
            invalidEmails: invalidEmails,
            errors: invalidEmails.length > 0 ? [`Invalid email(s): ${invalidEmails.join(', ')}`] : []
        };
    };

    // Validate emails whenever value changes
    useEffect(() => {
        const validation = validateEmailAddresses(value);
        setValidationState(validation);
    }, [value, required]);

    // Handle input change
    const handleInputChange = (e) => {
        const inputValue = e.target.value.toLowerCase(); // Convert to lowercase
        if (onChange) {
            onChange(inputValue, validationState);
        }
    };

    // Get validation status for display
    const getValidationStatus = () => {
        if (!value || value.trim() === '') {
            return { count: 0, status: 'empty' };
        }
        
        return {
            count: validationState.emails.length,
            invalidCount: validationState.invalidEmails.length,
            status: validationState.isValid ? 'valid' : 'invalid',
            validEmails: validationState.emails,
            invalidEmails: validationState.invalidEmails
        };
    };

    const status = getValidationStatus();
    const hasError = !validationState.isValid;

    return (
        <div className={`email-input-container ${className}`} style={style}>
            <input
                type="text"
                value={value ? value.toLowerCase() : ''}
                onChange={handleInputChange}
                placeholder={placeholder}
                readOnly={readOnly}
                required={required}
                className={`email-input ${hasError ? 'email-input-error' : ''}`}
                style={{ textTransform: 'lowercase', ...style }}
                {...rest}
            />
            
            {/* Validation Error Messages */}
            {hasError && validationState.errors.length > 0 && (
                <div className="email-error-messages">
                    {validationState.errors.map((error, index) => (
                        <div key={index} className="email-error-message">
                            {error}
                        </div>
                    ))}
                </div>
            )}

            {/* Validation Status Display */}
            {showValidationStatus && value && value.trim() !== '' && (
                <div className="email-validation-status">
                    {status.count > 0 && (
                        <span className="email-status-valid">
                            ✓ {status.count} valid email{status.count !== 1 ? 's' : ''}
                        </span>
                    )}
                    {status.invalidCount > 0 && (
                        <span className="email-status-invalid">
                            ✗ {status.invalidCount} invalid email{status.invalidCount !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            )}

            {/* Help Text */}
            {showHelpText && (
                <div className="email-help-text">
                    Enter multiple emails separated by commas (,) or semicolons (;)
                </div>
            )}
        </div>
    );
};

export default EmailInput;